import { isPlainObject, has, isUndefined } from 'lodash';

/**
 * convert props of type 'array/object' to 'string', based on the list of props to be converted in vizProps
 * @method convertPropsToString
 * @param {Object} vizProps
 * @param {Array} propNames
 * @return {Object} convertedVizProps
 */
export const convertPropsToString = ({ vizProps = {}, propNames = [] }) => {
    const convertedVizProps = {};

    propNames.forEach(propName => {
        const propValue = vizProps[propName];
        if (Array.isArray(propValue) || isPlainObject(propValue)) {
            convertedVizProps[propName] = JSON.stringify(propValue);
        } else if (propValue && typeof propValue === 'string') {
            // eslint-disable-next-line no-console
            console.warn(`The option type 'string' for '${propName}' has been deprecated.`);
        }
    });

    return convertedVizProps;
};

/**
 * Convert a hex value to a color
 * @method formatColor
 * @param {String} color
 * @returns {String}
 */
export const formatColor = color => {
    if (!color || typeof color !== 'string') {
        return '';
    }

    return color.replace('0x', '#');
};

/**
 * Helper for mapping the option properties (and values) we expose in config.ts into property values that splunk-charting accepts
 * This is for options that has new key name and can not directly be mapped with SimpleXML or
 * don't have a 1-1 mapping in SimpleXML (i.e. a straightforward name mapping where values are preserved)
 * @method convertToAreaSCProperties
 * @param {Object} originalOptions
 * @param {Object} optionsSCMapping
 * @param {Object} optionValuesSCMapping
 * @param {Array} stringValues
 * @returns {Object}
 */
export const convertToSCProperties = (
    originalOptions,
    optionsSCMapping,
    optionValuesSCMapping,
    stringValues
) => {
    const returnOptions = {};
    const { y2Fields, overlayFields } = originalOptions;

    // mapToOldKey
    Object.keys(originalOptions).forEach(key => {
        const newKey = optionsSCMapping[key] ? optionsSCMapping[key] : key;
        returnOptions[newKey] = originalOptions[key];
    });

    // mapToValues
    Object.keys(optionValuesSCMapping).forEach(key => {
        if (has(returnOptions, key)) {
            const updatedValue = optionValuesSCMapping[key][returnOptions[key]];
            if (!isUndefined(updatedValue)) {
                returnOptions[key] = updatedValue;
            }
        }
    });

    // convertY2AxisOptions
    if (y2Fields) {
        delete returnOptions.y2Fields;
        returnOptions['axisY2.enabled'] = true;
        returnOptions['axisY2.fields'] = Array.isArray(y2Fields)
            ? y2Fields.filter(name => name).join(',')
            : y2Fields;
    }

    // convertOverlayFields
    if (overlayFields) {
        returnOptions.overlayFields = Array.isArray(overlayFields)
            ? overlayFields
                  .filter(field => field)
                  .map(field => field.trim())
                  .join(',')
            : overlayFields;
    }

    // convertToStringValues
    stringValues.forEach(propName => {
        const propValue = returnOptions[propName];
        if (Array.isArray(propValue) || isPlainObject(propValue)) {
            returnOptions[propName] = JSON.stringify(propValue);
        } else if (propValue && typeof propValue === 'string') {
            // eslint-disable-next-line no-console
            console.warn(`The option type 'string' for '${propName}' has been deprecated.`);
        }
    });
    return returnOptions;
};

/**
 * Helper for removing the option properties (and values) that are not part of schema
 * @method adjustOptionsToSchema
 * @param {Object} originalOptions
 * @param {Object} optionsSchema
 * @returns {Object}
 */
export const adjustOptionsToSchema = (originalOptions, optionsSchema) => {
    const returnOptions = {};
    Object.keys(optionsSchema).forEach(key => {
        if (Object.hasOwn(originalOptions, key)) {
            returnOptions[key] = originalOptions[key];
        }
    });
    return returnOptions;
};
